import { useState } from 'react';

export default function InteractiveRoadmap({ audit, onUpdate, isClientView = false, visiblePhases = null }) {
  const ai = audit.aiResearch || {};
  const roadmapData = audit.roadmapProgress || {};
  const roadmapComments = audit.roadmapComments || [];
  const [newComment, setNewComment] = useState('');
  const [commentPhase, setCommentPhase] = useState('general');

  const phases = [
    {
      key: 'immediate',
      phase: '0–30 Days',
      label: 'Quick Wins',
      color: '#34d399',
      items: ai.roadmap?.immediate || ['Improve weakest pillar scores', 'Address critical operational gaps'],
    },
    {
      key: 'shortTerm',
      phase: '30–90 Days',
      label: 'Strategic Build',
      color: '#fbbf24',
      items: ai.roadmap?.shortTerm || ['Strengthen brand coherence', 'Develop signature guest moments'],
    },
    {
      key: 'mediumTerm',
      phase: '90–180 Days',
      label: 'Scale & Sustain',
      color: '#C9A668',
      items: ai.roadmap?.mediumTerm || ['Re-audit and measure progress', 'Build scalable growth systems'],
    },
  ];

  const toggleItem = (phaseKey, itemIndex) => {
    const progress = { ...roadmapData };
    if (!progress[phaseKey]) progress[phaseKey] = {};
    progress[phaseKey][itemIndex] = !progress[phaseKey]?.[itemIndex];
    if (onUpdate) onUpdate({ ...audit, roadmapProgress: progress });
  };

  const addComment = () => {
    if (!newComment.trim()) return;
    const comment = {
      id: `rc_${Date.now()}`,
      phase: commentPhase,
      text: newComment.trim(),
      author: isClientView ? 'Client' : 'Team',
      createdAt: new Date().toISOString(),
    };
    const updated = [...roadmapComments, comment];
    if (onUpdate) onUpdate({ ...audit, roadmapComments: updated });
    setNewComment('');
  };

  const deleteComment = (commentId) => {
    const updated = roadmapComments.filter(c => c.id !== commentId);
    if (onUpdate) onUpdate({ ...audit, roadmapComments: updated });
  };

  const getPhaseProgress = (phaseKey, itemCount) => {
    if (!roadmapData[phaseKey] || itemCount === 0) return 0;
    const completed = Object.values(roadmapData[phaseKey]).filter(Boolean).length;
    return Math.round((completed / itemCount) * 100);
  };

  // Filter phases by visibility toggles
  const filteredPhases = visiblePhases
    ? phases.filter((p, i) => visiblePhases[i] !== false)
    : phases;

  const totalItems = filteredPhases.reduce((sum, p) => sum + p.items.filter(Boolean).length, 0);
  const totalCompleted = filteredPhases.reduce((sum, p) => {
    if (!roadmapData[p.key]) return sum;
    return sum + Object.values(roadmapData[p.key]).filter(Boolean).length;
  }, 0);
  const overallProgress = totalItems > 0 ? Math.round((totalCompleted / totalItems) * 100) : 0;

  return (
    <div>
      {/* Overall progress */}
      <div className="card p-5 mb-4">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-display text-xl text-white">The Next Experience Roadmap</h2>
          <div className="text-right">
            <span className="text-2xl font-black text-brand-gold">{overallProgress}%</span>
            <p className="text-[10px] text-neutral-500">Complete</p>
          </div>
        </div>
        <div className="h-2 bg-neutral-800 rounded-full overflow-hidden">
          <div className="h-full bg-brand-gold rounded-full transition-all duration-500" style={{ width: `${overallProgress}%` }} />
        </div>
        <p className="text-[10px] text-neutral-600 mt-1">{totalCompleted} of {totalItems} items completed</p>
      </div>

      {/* Phases */}
      <div className="space-y-4">
        {filteredPhases.map(phase => {
          const validItems = phase.items.filter(Boolean);
          const phaseProgress = getPhaseProgress(phase.key, validItems.length);
          const phaseComments = roadmapComments.filter(c => c.phase === phase.key);

          return (
            <div key={phase.key} className="card p-5 border-l-4" style={{ borderLeftColor: phase.color }}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-white">{phase.phase}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: phase.color + '20', color: phase.color }}>
                    {phase.label}
                  </span>
                </div>
                <span className="text-xs font-bold" style={{ color: phase.color }}>{phaseProgress}%</span>
              </div>

              <div className="h-1 bg-neutral-800 rounded-full overflow-hidden mb-3">
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${phaseProgress}%`, backgroundColor: phase.color }} />
              </div>

              {/* Checkable items */}
              <div className="space-y-2 mb-3">
                {validItems.map((item, i) => {
                  const checked = roadmapData[phase.key]?.[i] || false;
                  return (
                    <label key={i} className="flex items-start gap-3 cursor-pointer group">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleItem(phase.key, i)}
                        className="mt-0.5 rounded border-neutral-600 bg-neutral-800 text-brand-gold focus:ring-brand-gold/30"
                      />
                      <span className={`text-sm transition-all ${checked ? 'text-neutral-600 line-through' : 'text-neutral-300 group-hover:text-white'}`}>
                        {typeof item === 'string' ? item : item.description || JSON.stringify(item)}
                      </span>
                    </label>
                  );
                })}
              </div>

              {/* Phase comments */}
              {phaseComments.length > 0 && (
                <div className="border-t border-neutral-800 pt-3 mt-3 space-y-2">
                  {phaseComments.map(c => (
                    <div key={c.id} className="bg-neutral-800/30 rounded-lg px-3 py-2">
                      <p className="text-xs text-neutral-300">{c.text}</p>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-[10px] text-neutral-600">{c.author} — {new Date(c.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>
                        {onUpdate && <button onClick={() => deleteComment(c.id)} className="text-[10px] text-neutral-600 hover:text-red-400">Delete</button>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add comment */}
      {onUpdate && (
        <div className="card p-4 mt-4">
          <p className="text-xs text-neutral-500 mb-2">{isClientView ? 'Add a comment or question' : 'Add a note'}</p>
          <div className="flex gap-2 mb-2">
            <select value={commentPhase} onChange={(e) => setCommentPhase(e.target.value)} className="input-field text-sm py-1.5 w-32">
              <option value="general">General</option>
              {phases.map(p => <option key={p.key} value={p.key}>{p.phase}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder={isClientView ? 'Comment or question about this action item...' : 'Add a note...'}
              className="input-field text-sm flex-1"
              onKeyDown={(e) => e.key === 'Enter' && addComment()}
            />
            <button onClick={addComment} disabled={!newComment.trim()} className="btn-primary text-xs py-2 disabled:opacity-40">Add</button>
          </div>
        </div>
      )}

      {overallProgress === 100 && (
        <div className="card p-5 mt-4 border border-brand-gold/30 text-center">
          <p className="text-brand-gold font-display text-lg mb-1">Roadmap Complete</p>
          <p className="text-neutral-400 text-sm">Time for a re-audit to measure your progress.</p>
        </div>
      )}
    </div>
  );
}
